import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Clock, ChevronLeft, ChevronRight, Tv } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ContinueWatching = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [items, setItems] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchContinueWatching();
    fetchRecommendations();
  }, []);

  const fetchContinueWatching = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setIsLoading(false); return; }
      const response = await axios.get(`${API}/api/user/continue-watching`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setItems(response.data.items || []);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/content/recommended/personalized?limit=12`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRecommendations(response.data || []);
    } catch {
      // silent
    }
  };

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  const isLoggedIn = !!localStorage.getItem('token');
  if (isLoading && isLoggedIn) return null;

  // Not logged in — show recommendations
  if (!isLoggedIn) {
    if (recommendations.length === 0) return null;
    return (
      <section className="py-8 sm:py-10" data-testid="recommended-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <Play className="w-5 h-5 text-emerald-500 shrink-0" />
              <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">Recommended For You</h2>
            </div>
            <div className="hidden sm:flex items-center gap-1">
              <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors" aria-label="Scroll left">
                <ChevronLeft className="w-4 h-4 text-white/50" />
              </button>
              <button onClick={() => scroll(1)} className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors" aria-label="Scroll right">
                <ChevronRight className="w-4 h-4 text-white/50" />
              </button>
            </div>
          </div>
          <div ref={scrollRef} className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            {recommendations.slice(0, 12).map((m) => (
              <div
                key={m.id}
                onClick={() => navigate(`/watch/${m.id}`)}
                className="flex-shrink-0 w-[240px] sm:w-[280px] group cursor-pointer snap-start"
                data-testid={`recommended-${m.id}`}
              >
                <div className="relative aspect-video rounded-lg overflow-hidden mb-2">
                  <img src={m.thumbnail_url} alt={m.teams || m.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center"><Play className="w-4 h-4 text-white fill-white" /></div>
                  </div>
                  {m.competition_name && <div className="absolute bottom-2 left-2 text-emerald-400 text-[8px] font-bold uppercase">{m.competition_name}</div>}
                </div>
                <h3 className="text-white font-semibold text-sm group-hover:text-emerald-400 transition-colors line-clamp-1">{m.teams || m.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Logged in but no history — onboarding
  if (items.length === 0) {
    if (recommendations.length === 0) return null;
    return (
      <section className="py-8 sm:py-10" data-testid="continue-watching-empty">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-5">
            <Clock className="w-5 h-5 text-emerald-500 shrink-0" />
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">Continue Watching</h2>
          </div>
          <div className="rounded-xl border border-white/5 bg-white/[0.02] px-6 py-6 text-center mb-5">
            <Tv className="w-7 h-7 text-emerald-500/60 mx-auto mb-2" />
            <p className="text-white/70 font-medium text-sm">Start watching to build your history</p>
          </div>
          <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Recommended for you</p>
          <div ref={scrollRef} className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            {recommendations.slice(0, 8).map((m) => (
              <div key={m.id} onClick={() => navigate(`/watch/${m.id}`)} className="flex-shrink-0 w-[240px] sm:w-[280px] group cursor-pointer snap-start" data-testid={`recommended-${m.id}`}>
                <div className="relative aspect-video rounded-lg overflow-hidden mb-2">
                  <img src={m.thumbnail_url} alt={m.teams || m.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center"><Play className="w-4 h-4 text-white fill-white" /></div>
                  </div>
                </div>
                <h3 className="text-white font-semibold text-sm group-hover:text-emerald-400 transition-colors line-clamp-1">{m.teams || m.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Has watch history — continue watching with progress bars
  return (
    <section className="py-8 sm:py-10" data-testid="continue-watching-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-emerald-500 shrink-0" />
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">Continue Watching</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1">
              <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors" aria-label="Scroll left">
                <ChevronLeft className="w-4 h-4 text-white/50" />
              </button>
              <button onClick={() => scroll(1)} className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors" aria-label="Scroll right">
                <ChevronRight className="w-4 h-4 text-white/50" />
              </button>
            </div>
            <button onClick={() => navigate('/following')} className="text-emerald-500 hover:text-emerald-400 text-xs font-semibold tracking-wide uppercase transition-colors" data-testid="continue-watching-see-all">See All</button>
          </div>
        </div>

        <div ref={scrollRef} className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
          {items.map((item) => {
            const resumeMin = Math.floor((item.resume_position || 0) / 60);
            const resumeSec = String(Math.floor((item.resume_position || 0) % 60)).padStart(2, '0');
            const isLive = item.is_live;
            const resumeUrl = isLive
              ? `/match/${item.id}?t=${Math.floor(item.resume_position || 0)}`
              : (item.category === 'match'
                ? `/match/${item.id}?t=${Math.floor(item.resume_position || 0)}`
                : `/watch/${item.id}?t=${Math.floor(item.resume_position || 0)}`);

            return (
              <div
                key={item.id}
                className="flex-shrink-0 w-[260px] sm:w-[300px] group cursor-pointer snap-start"
                onClick={() => navigate(resumeUrl)}
                data-testid={`continue-watching-${item.id}`}
              >
                <div className="relative aspect-video rounded-lg overflow-hidden mb-2">
                  <img src={item.thumbnail_url} alt={item.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  {/* Progress bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/20">
                    <div className="h-full bg-emerald-500 rounded-r transition-all" style={{ width: `${item.progress_percent || 0}%` }} />
                  </div>
                  {/* Status badge */}
                  <div className="absolute top-2 left-2">
                    {isLive ? (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold text-white bg-red-600 rounded animate-pulse">LIVE</span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium text-white/80 bg-black/50 backdrop-blur-sm rounded">{resumeMin}:{resumeSec}</span>
                    )}
                  </div>
                  {/* Play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-11 h-11 bg-emerald-600/90 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <Play className="w-4 h-4 text-white fill-white" />
                    </div>
                  </div>
                </div>
                <h3 className="text-white font-medium text-sm truncate group-hover:text-emerald-400 transition-colors">{item.teams || item.title}</h3>
                <p className="text-white/40 text-[11px]">{isLive ? 'Rejoin live' : `Resume from ${resumeMin}:${resumeSec}`}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ContinueWatching;
